import { Text, View } from 'react-native';
import type { EffortScale, WorkoutExerciseEntry } from '@heraion/shared';

import { Icon } from '../index';
import type { TFn } from '../i18nTypes';

import type { useTheme } from './theme';
import type { WeightUnit } from './state/loggerDraft';
import { formatShortDate } from '@heraion/shared';
import { machineLabel } from './machineLabel';
import { rowsFromSessions, type HistorySetRow } from './historySetRows';
import { HistorySetLine } from './_HistorySetLine';
import { HistoryCopyRow } from './_HistoryCopyRow';
import type { HistoryPanelStyles } from './HistoryPanel.styles';
import { useDateLocale } from '../primitives/DateFormatContext';

// ⭐ Extracted from HistoryPanel.tsx when `PG-11`'s header row pushed that
// file to 509 lines. Section 1 already lives in `_WeightCandidateSection.tsx`
// for exactly the same reason, so section 3 follows the convention rather
// than inventing a second one.

// Section 3: one similar exercise, its latest session, set by set.
export function EstimateBlock({
  session,
  sourceName,
  locale,
  unit,
  effortScale,
  styles,
  theme,
  t,
  copyLabel,
  copySetA11y,
  copyOfSet,
}: {
  session: { startedAt: string; entry: WorkoutExerciseEntry };
  sourceName: string;
  locale: string;
  unit: WeightUnit;
  effortScale: EffortScale;
  styles: HistoryPanelStyles;
  theme: ReturnType<typeof useTheme>;
  t: TFn;
  copyLabel: string;
  copySetA11y: string;
  copyOfSet: ((row: HistorySetRow) => () => void) | null;
}) {
  const dateLocale = useDateLocale();
  // Same normaliser as the real history above, so an estimate row and a
  // history row can never render the same set two different ways.
  const rows = rowsFromSessions([
    {
      sessionId: session.startedAt,
      startedAt: session.startedAt,
      entry: session.entry,
      topE1RM: 0,
    },
  ]);
  return (
    <View style={styles.rowMain}>
      <Text style={styles.sourceName} numberOfLines={1}>
        {t('mobile.logger.historyEstimatedFrom', {
          defaultValue: 'From {{name}}',
          name: sourceName,
        })}
      </Text>
      <View style={styles.rowDateLine}>
        <Text style={styles.rowDate}>
          {formatShortDate(session.startedAt, dateLocale)}
        </Text>
        {/* EQ-1 — which machine that session was on. Display-only: changing
            machine is replace-exercise, the one place the tag is managed. */}
        {session.entry.equipmentInstance ? (
          <View style={styles.machineTag}>
            <Icon name="tag" size={11} color={theme.palette.ink3} />
            <Text style={styles.machineTagText}>
              {machineLabel(session.entry.equipmentInstance, t)}
            </Text>
          </View>
        ) : null}
      </View>
      {rows.map((row, idx) => (
        <HistoryCopyRow
          key={`${row.atMs}-${idx}`}
          styles={styles}
          theme={theme}
          copyLabel={copyLabel}
          copyA11yLabel={copySetA11y}
          onCopy={copyOfSet ? copyOfSet(row) : undefined}
        >
          <HistorySetLine
            row={row}
            locale={locale}
            unit={unit}
            effortScale={effortScale}
            styles={styles}
            t={t}
          />
        </HistoryCopyRow>
      ))}
    </View>
  );
}
